/**
 * The standalone vocabulary-pack document — a versioned wrapper around one
 * authored lexicon (decision D16; R2 ruling, 2026-08-13).
 *
 * @remarks
 * Game packs never use this shape: their vocabulary rides inside the
 * content-pack document, whose manifest already carries the `schemaVersion`
 * gate. A REFERENCE vocabulary pack (e.g. `standard-english-vocabulary` in
 * `@orpheus/content-packs`) ships as its own JSON document, and every
 * top-level authored artifact must pass the exact-match version gate — so the
 * raw `LexiconDefinition` gains this minimal wrapper rather than a version
 * field of its own, which would break the 1:1 parser mirror in
 * `./vocabulary.ts`.
 *
 * Growth path: if vocabulary packs ever need identity or provenance (a
 * registry id, a title for the creator studio), those fields join this
 * wrapper — the lexicon itself stays a pure parser mirror forever.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { contentSchemaVersionSchema } from './versioning.js';
import { lexiconDefinitionSchema } from './vocabulary.js';

/** One standalone, versioned vocabulary-pack document. */
export const vocabularyPackDefinitionSchema = z
	.strictObject({
		lexicon: lexiconDefinitionSchema,
		schemaVersion: contentSchemaVersionSchema,
	})
	.readonly();

/** One standalone vocabulary pack — see {@link vocabularyPackDefinitionSchema}. */
export type VocabularyPackDefinition = z.infer<typeof vocabularyPackDefinitionSchema>;
